import React from 'react';
import TreeNode from './TreeNode';
import { CAMERA_CONFIG } from './camera/cameraConfig';
import { calculateNodePositions } from './camera/layoutHelpers';
import { CameraContainer, WorldContainer, NodeWrapper } from './camera/CameraTreeVisualization.styles';
import MinimapComponent from './camera/MinimapComponent';
import CameraControls from './camera/CameraControls';
import ConnectionsRenderer from './camera/ConnectionsRenderer';
import MobileChildLabels from './camera/MobileChildLabels';
import XPProgressBar from './camera/XPProgressBar';
import AdminNodeButtons from './camera/AdminNodeButtons';
import { createEventHandlers } from './camera/cameraEventHandlers';
import {
  focusOnNode,
  resetCamera,
  centerCameraOnSelectedNode,
  updateCameraFromKeyboard
} from './camera/cameraAnimations';
import {
  updateVisibleNodes,
  findMiddleNodeOfFirstRow,
  handleSearchChange,
  selectSearchResult,
  getParentPath,
  toggleSearch,
  toggleNavigation,
  toggleMinimap,
  toggleControls,
  getCameraState,
  setCameraState,
  resetToFirstNode
} from './camera/cameraUtils';

class CameraTreeVisualization extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      camera: {
        x: CAMERA_CONFIG.INITIAL_CAMERA_X_OFFSET,
        y: CAMERA_CONFIG.INITIAL_CAMERA_Y_OFFSET,
        scale: CAMERA_CONFIG.INITIAL_ZOOM
      },
      isPanning: false,
      lastPanPoint: { x: 0, y: 0 },
      nodePositions: new Map(),
      visibleNodes: new Set(),
      selectedNodeId: props.selectedNodeId || null,
      zoomStep: 1, // Re-clicking the already-focused card advances this (see focusOnNode)
      searchQuery: '',
      searchResults: [],
      showSearch: false,
      treeBounds: [],
      navigationCollapsed: false,
      minimapCollapsed: typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768,
      // Search/overview/focus controls start open everywhere (incl. mobile)
      controlsCollapsed: false,
      touchStartDistance: null,
      lastTouchCenter: { x: 0, y: 0 },
      minZoomLevel: 0.1, // Will be calculated based on tree size
      // Momentum scrolling
      momentum: { vx: 0, vy: 0 },
      lastMoveTime: Date.now(),
      panHistory: [], // Track recent pan movements for velocity calculation
      flashingLineId: null, // Track which connection line is flashing yellow
      isActivelyZooming: false, // Track if user is actively zooming (for performance optimization)
      zoomSettling: false // Keep node transitions suspended through the zoom/transform mode switch
    };

    this.containerRef = React.createRef();
    this.worldRef = React.createRef();
    this.animationFrame = null;
    this.cameraAnimation = null;
    this.momentumAnimation = null;
    this.zoomIdleTimeout = null; // Timeout to detect when zooming stops
    this.zoomSettleTimeout = null; // Timeout to re-enable node transitions after mode switch
    this.anchorNodeId = null; // Node that should stay fixed (using ref for sync access)
    this.keys = new Set();

    // Bind event handlers
    const handlers = createEventHandlers(this);
    this.handleMouseDown = handlers.handleMouseDown.bind(this);
    this.handleMouseMove = handlers.handleMouseMove.bind(this);
    this.handleMouseUp = handlers.handleMouseUp.bind(this);
    this.handleWheel = handlers.handleWheel.bind(this);
    this.handleTouchStart = handlers.handleTouchStart.bind(this);
    this.handleTouchMove = handlers.handleTouchMove.bind(this);
    this.handleTouchEnd = handlers.handleTouchEnd.bind(this);
    this.handleKeyDown = handlers.handleKeyDown.bind(this);
    this.handleKeyUp = handlers.handleKeyUp.bind(this);

    // Bind utility methods
    this.updateCamera = this.updateCamera.bind(this);
    this.recalculateNodePositions = this.recalculateNodePositions.bind(this);
    this.setContainerRef = this.setContainerRef.bind(this);
    this.preventGesture = this.preventGesture.bind(this);
  }

  // iOS Safari drives pinch-zoom through proprietary gesture events that
  // ignore touch-action; cancelling them is the only reliable way to stop
  // the page zoom from fighting the camera zoom.
  preventGesture(e) {
    e.preventDefault();
  }

  // React registers touch/wheel props as passive listeners, so
  // preventDefault() inside them is ignored and the browser's native
  // pinch-zoom/scroll runs alongside the camera. Attach non-passive
  // native listeners instead. The container only mounts once tree data
  // is loaded, so a ref callback (not componentDidMount) handles attach.
  setContainerRef(node) {
    if (this.containerRef.current === node) return;
    if (this.containerRef.current) {
      this.detachContainerListeners(this.containerRef.current);
    }
    this.containerRef.current = node;
    if (node) {
      this.attachContainerListeners(node);
    }
  }

  attachContainerListeners(node) {
    node.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    node.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    node.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    node.addEventListener('wheel', this.handleWheel, { passive: false });
    node.addEventListener('gesturestart', this.preventGesture);
    node.addEventListener('gesturechange', this.preventGesture);
    node.addEventListener('gestureend', this.preventGesture);
  }

  detachContainerListeners(node) {
    node.removeEventListener('touchstart', this.handleTouchStart);
    node.removeEventListener('touchmove', this.handleTouchMove);
    node.removeEventListener('touchend', this.handleTouchEnd);
    node.removeEventListener('wheel', this.handleWheel);
    node.removeEventListener('gesturestart', this.preventGesture);
    node.removeEventListener('gesturechange', this.preventGesture);
    node.removeEventListener('gestureend', this.preventGesture);
  }

  componentDidMount() {
    // StrictMode (dev) simulates an unmount/remount: componentWillUnmount
    // detaches the native listeners but the DOM ref callback is not invoked
    // again on remount, so re-attach here (detach first to stay idempotent).
    if (this.containerRef.current) {
      this.detachContainerListeners(this.containerRef.current);
      this.attachContainerListeners(this.containerRef.current);
    }

    this.recalculateNodePositions();

    // Start with overview, then focus on level 0 root node with animation
    setTimeout(() => {
      resetCamera(this);
      setTimeout(() => {
        const { allNodes } = this.props;
        const rootNode = allNodes?.find(node => node.level === 0);

        if (rootNode) {
          focusOnNode(this, rootNode.id);
        } else {
          const middleNodeId = findMiddleNodeOfFirstRow(allNodes);
          if (middleNodeId) {
            focusOnNode(this, middleNodeId);
          } else if (this.state.selectedNodeId) {
            centerCameraOnSelectedNode(this);
          }
        }
      }, 500);
    }, 100);

    updateVisibleNodes(this);

    // Add event listeners
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    // Start camera update loop
    this.updateCamera();
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    if (this.containerRef.current) {
      this.detachContainerListeners(this.containerRef.current);
    }
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    if (this.cameraAnimation) {
      cancelAnimationFrame(this.cameraAnimation);
    }
    if (this.momentumAnimation) {
      cancelAnimationFrame(this.momentumAnimation);
    }
    if (this.zoomIdleTimeout) {
      clearTimeout(this.zoomIdleTimeout);
    }
    if (this.zoomSettleTimeout) {
      clearTimeout(this.zoomSettleTimeout);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.allNodes !== this.props.allNodes) {
      this.recalculateNodePositions();
      updateVisibleNodes(this);
    }

    if (prevProps.selectedNodeId !== this.props.selectedNodeId) {
      this.setState({ selectedNodeId: this.props.selectedNodeId });
      if (this.props.selectedNodeId) {
        focusOnNode(this, this.props.selectedNodeId);
      }
    }
  }

  recalculateNodePositions() {
    const { allNodes } = this.props;
    if (!allNodes || allNodes.length === 0) return;

    const { selectedNodeId, nodePositions: oldPositions } = this.state;

    // Use anchor node (from expand/collapse) or fall back to selected node
    const lockNodeId = this.anchorNodeId || selectedNodeId;

    // Store old position of the node we want to keep fixed
    const oldNodePos = lockNodeId && oldPositions.has(lockNodeId)
      ? oldPositions.get(lockNodeId)
      : null;

    const { positions, treeBounds } = calculateNodePositions(allNodes);

    // If we have an anchor node, adjust ALL positions so anchor stays in place
    if (lockNodeId && oldNodePos && positions.has(lockNodeId)) {
      const newNodePos = positions.get(lockNodeId);
      // Calculate how much the anchor node moved
      const deltaX = newNodePos.x - oldNodePos.x;
      const deltaY = newNodePos.y - oldNodePos.y;

      // Shift all node positions by -delta so anchor node stays at its old position
      if (deltaX !== 0 || deltaY !== 0) {
        positions.forEach((pos, nodeId) => {
          positions.set(nodeId, {
            x: pos.x - deltaX,
            y: pos.y - deltaY
          });
        });
      }
    }

    // Clear anchor after use
    this.anchorNodeId = null;

    this.setState({
      nodePositions: positions,
      treeBounds
    }, () => {
      this.calculateMinZoomLevel();
    });
  }

  calculateMinZoomLevel() {
    const container = this.containerRef.current;
    const { nodePositions } = this.state;

    if (!container || !nodePositions || nodePositions.size === 0) {
      this.setState({ minZoomLevel: 0.1 });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const positions = Array.from(nodePositions.values());
    const minX = Math.min(...positions.map(pos => pos.x));
    const maxX = Math.max(...positions.map(pos => pos.x));
    const minY = Math.min(...positions.map(pos => pos.y));
    const maxY = Math.max(...positions.map(pos => pos.y));

    const worldWidth = maxX - minX + 800;
    const worldHeight = maxY - minY + 600;

    const scaleX = containerRect.width / worldWidth;
    const scaleY = containerRect.height / worldHeight;
    const overviewScale = Math.min(scaleX, scaleY, 0.5) * 0.85;

    // Respect MIN_ZOOM from config (lowered for landscape usability)
    const minZoom = Math.max(overviewScale, CAMERA_CONFIG.MIN_ZOOM);
    this.setState({ minZoomLevel: minZoom });
  }

  updateCamera() {
    updateCameraFromKeyboard(this);
    updateVisibleNodes(this);
    this.animationFrame = requestAnimationFrame(this.updateCamera);
  }

  // Card click: a different card focuses normally (step 1); re-clicking the
  // card already focused zooms in one more step instead of doing nothing.
  handleCardClick = (clickedNode) => {
    if (clickedNode.id === this.state.selectedNodeId) {
      const nextStep = this.state.zoomStep + 1;
      focusOnNode(this, clickedNode.id, nextStep);
    } else {
      this.props.onNodeClick(clickedNode);
    }
  };

  // Public API methods (used by parent components)
  focusOnNode = (nodeId) => focusOnNode(this, nodeId);
  navigateToNode = (nodeId) => {
    const allNodes = this.props.allNodesUnfiltered || this.props.allNodes || [];
    const node = allNodes.find(n => n.id === nodeId);
    if (!node) return;
    selectSearchResult(this, { ...node, parentPath: getParentPath(node, allNodes) });
  };
  resetCamera = () => resetCamera(this);
  resetToFirstNode = () => resetToFirstNode(this);
  getCameraState = () => getCameraState(this);
  setCameraState = (cameraState) => setCameraState(this, cameraState);
  toggleSearch = () => toggleSearch(this);
  toggleNavigation = () => toggleNavigation(this);
  toggleMinimap = () => toggleMinimap(this);
  toggleControls = () => toggleControls(this);

  render() {
    const {
      allNodes,
      onNodeClick,
      onShowDetails,
      isFilteringFavorites,
      isFilteringProgress,
      favoriteIds,
      isProgressiveMode,
      expandedNodes,
      onToggleExpansion,
      allNodesUnfiltered
    } = this.props;
    const {
      camera,
      nodePositions,
      visibleNodes,
      selectedNodeId,
      showSearch,
      searchQuery,
      searchResults,
      minimapCollapsed,
      controlsCollapsed
    } = this.state;

    if (!allNodes || allNodes.length === 0) {
      return (
        <CameraContainer>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            color: 'white',
            fontSize: '1.2rem'
          }}>
            {this.props.translations?.loadingTree || "Loading sleep ideas..."}
          </div>
        </CameraContainer>
      );
    }

    // Mobile filtering is now done in App.js (like favorites/progress filtering)
    // allNodes already contains the filtered nodes on mobile
    const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768;
    const nodesToRender = allNodes;

    const container = this.containerRef.current;
    const containerRect = container ? container.getBoundingClientRect() : null;

    return (
      <CameraContainer
        ref={this.setContainerRef}
        isPanning={this.isPanning}
        onMouseDown={this.handleMouseDown}
        onMouseMove={this.handleMouseMove}
        onMouseUp={this.handleMouseUp}
      >
        {/* TreeNavigation removed - simplified mobile UI */}

        <WorldContainer
          ref={this.worldRef}
          style={this.state.isActivelyZooming
            ? {
                // During active zoom: use transform for smooth performance
                transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.scale})`
              }
            : {
                // When idle: use CSS zoom for crisp text
                transform: `translate(${camera.x / camera.scale}px, ${camera.y / camera.scale}px)`,
                zoom: camera.scale
              }
          }
        >
          <ConnectionsRenderer
            allNodes={nodesToRender}
            nodePositions={nodePositions}
            flashingLineId={this.state.flashingLineId}
            onLineClick={(targetNodeId, otherNodeId) => {
              const isMobile = typeof window !== 'undefined' && Math.min(window.innerWidth, window.innerHeight) <= 768;
              
              // Determine parent and child for lineId (always parent-child format)
              const targetNode = allNodes.find(n => n.id === targetNodeId);
              const otherNode = allNodes.find(n => n.id === otherNodeId);
              let parentId, childId;
              
              if (targetNode && otherNode) {
                if (targetNode.parentId === otherNodeId) {
                  // target is child, other is parent
                  parentId = otherNodeId;
                  childId = targetNodeId;
                } else {
                  // target is parent, other is child
                  parentId = targetNodeId;
                  childId = otherNodeId;
                }
                
                // Flash the line yellow
                const lineId = `${parentId}-${childId}`;
                this.setState({ flashingLineId: lineId });
                setTimeout(() => {
                  this.setState({ flashingLineId: null });
                }, 800); // Flash for 0.8 second
              }
              
              // Mobile only: Close ALL children of current node (recursively)
              if (isMobile && selectedNodeId) {
                const closeAllChildren = (nodeId) => {
                  const children = allNodes.filter(n => n.parentId === nodeId);
                  children.forEach(child => {
                    // Close this child's children first (recursive)
                    closeAllChildren(child.id);
                    // Then close this child if it's expanded
                    if (expandedNodes && expandedNodes.has(child.id) && this.props.onToggleExpansion) {
                      this.props.onToggleExpansion(child.id);
                    }
                  });
                };
                closeAllChildren(selectedNodeId);
              }
              
              // Focus on target node (the one we want to select based on which half was clicked)
              this.focusOnNode(targetNodeId);
            }}
            visibleNodes={visibleNodes}
          />

          {nodesToRender.map(node => {
            const position = nodePositions.get(node.id);
            const isVisible = visibleNodes.has(node.id);

            if (!position || !isVisible) return null;

            // Always check children in the FULL tree (not filtered) to show expand arrows
            const nodeHasChildren = (allNodesUnfiltered || allNodes).some(n => n.parentId === node.id);
            const nodeIsExpanded = expandedNodes ? expandedNodes.has(node.id) : false;

            return (
              <NodeWrapper
                key={node.id}
                $visible={isVisible === true ? 1 : 0}
                $suspendTransition={(this.state.isActivelyZooming || this.state.zoomSettling) ? 1 : 0}
                style={{
                  left: `${position.x}px`,
                  top: `${position.y}px`
                }}
              >
                {this.props.isAdmin && (
                  <AdminNodeButtons
                    node={node}
                    onEdit={this.props.onEditNode}
                    onCreate={this.props.onCreateChild}
                    onDelete={this.props.onDeleteNode}
                  />
                )}

                <TreeNode
                  node={node}
                  allNodes={allNodesUnfiltered || allNodes}
                  isSelected={node.id === selectedNodeId}
                  onClick={this.handleCardClick}
                  onDetailsClick={onShowDetails}
                  levelType="current"
                  isParentOnly={(isFilteringFavorites || isFilteringProgress) && !favoriteIds.includes(node.id)}
                  hasChildren={nodeHasChildren}
                  isExpanded={nodeIsExpanded}
                  onToggleExpansion={(nodeId) => {
                    // Set anchor node so camera keeps this node fixed during position recalculation
                    this.anchorNodeId = nodeId;
                    // On mobile, expanding also focuses on the node
                    if (isMobile) {
                      onNodeClick(allNodes.find(n => n.id === nodeId));
                    }
                    if (onToggleExpansion) {
                      onToggleExpansion(nodeId);
                    }
                  }}
                  showExpandButton={isMobile ? nodeHasChildren : (isProgressiveMode && !isFilteringFavorites && !isFilteringProgress && node.level !== 0)}
                  onProgressToggle={this.props.onProgressToggle}
                  completedNodes={this.props.completedNodes}
                  zoomLevel={camera.scale}
                  isAdmin={this.props.isAdmin}
                  onShowStats={this.props.onShowStats}
                />

                {/* MobileChildLabels disabled on mobile - use expand button instead */}
                {!isMobile && (
                  <MobileChildLabels
                    node={node}
                    allNodes={allNodesUnfiltered || allNodes}
                    onNodeClick={this.focusOnNode}
                    expandedNodes={expandedNodes}
                  />
                )}
              </NodeWrapper>
            );
          })}
        </WorldContainer>

        <MinimapComponent
          nodePositions={nodePositions}
          camera={camera}
          selectedNodeId={selectedNodeId}
          collapsed={minimapCollapsed}
          containerRect={containerRect}
          onToggle={this.toggleMinimap}
          onNodeClick={this.focusOnNode}
          allNodes={allNodes}
          isMobile={this.props.isMobile}
        />

        <CameraControls
          collapsed={controlsCollapsed}
          onToggle={this.toggleControls}
          onToggleSearch={this.toggleSearch}
          onResetCamera={this.resetCamera}
          onFocusOnSelected={() => {
            if (this.state.selectedNodeId) {
              this.focusOnNode(this.state.selectedNodeId);
            }
          }}
          selectedNodeId={selectedNodeId}
          translations={this.props.translations}
          showSearch={showSearch}
          searchQuery={searchQuery}
          searchResults={searchResults}
          onSearchChange={(query) => handleSearchChange(this, query)}
          onSelectResult={(node) => selectSearchResult(this, node)}
        />

        <XPProgressBar
          completedNodes={this.props.completedNodes}
          totalNodes={this.props.allNodesUnfiltered ? this.props.allNodesUnfiltered.length : allNodes.length}
          isAuthenticated={this.props.isAuthenticated}
          isMobile={this.props.isMobile}
        />
      </CameraContainer>
    );
  }
}

export default CameraTreeVisualization;
